import type { Assertion } from '../lib/types';
import { label } from '../lib/format';
import { Pill } from './Pill';

/**
 * What a claim asks the reader to go and find, if anything.
 *
 * Duplicated from `EventRow` deliberately rather than shared: the two render
 * different records, and the shared thing between them is the grade itself,
 * which is already computed once in the domain and arrives on both.
 */
const NEEDED: Record<string, string> = {
  credible_source: 'Needs a credible source',
  independent_source: 'Needs an independent source',
  primary_record: 'Needs a primary record',
};

/**
 * One sourced claim about a company, with the grade its own sources support.
 *
 * The predicate and the value are shown together because a claim is the pair —
 * "latest_funding_stage is seed" — and either alone is unreadable. The grade
 * sits beside it because the point of holding competing claims about one
 * predicate is being able to see which is better attested; before it was
 * derived, two claims were told apart by a letter someone typed.
 */
export function ClaimRow({ claim }: { claim: Assertion }) {
  const attested = `${claim.evidenceGrade} · ${claim.independentSources} independent`;
  return (
    <article className="event-row">
      <div className="event-row__marks">
        <Pill value={claim.predicate} tone="accent" />
        <Pill value={attested} tone={claim.corroborationGap ? 'warning' : 'positive'} />
      </div>
      <div className="event-row__body">
        {/* A claim's value is arbitrary JSON, so it cannot be declared a
            taxonomy field and the repository-wide unlabelled-render sweep
            cannot watch it. Most values in practice are taxonomy members —
            this one arrived as `series_c_plus` — so a string is labelled and
            anything else is serialised rather than stringified, which keeps an
            object from rendering as "[object Object]". */}
        <strong>{typeof claim.value === 'string' ? label(claim.value) : JSON.stringify(claim.value)}</strong>
        <span>{label(claim.predicate)}</span>
        {claim.corroborationGap && (
          <small className="muted">{NEEDED[claim.corroborationGap]}</small>
        )}
      </div>
      <time>{claim.observedAt.slice(0, 10)}</time>
    </article>
  );
}
